import { computed, toRaw, watch, type Ref } from 'vue'

import type {
  RabbitIncidentMessage,
  SyncOptions,
} from '@collector/queue-gateway-shared-types'
import { Incident } from '@collector/sportsapi-client'
import { EventScout } from '@collector/sportsapi-client-legacy'
import { useAuth } from '@desktop/globalState/userAuth/useAuth'
import { queryClient } from '@desktop/queryClient'
import {
  filterUserIncidentsUpdate,
  type IncidentUpdate,
} from '@desktop/views/Relation/utils'

import { useEventIncidentsQuery } from './queries/useEventIncidentsQuery'
import { useEventIncidentsUpdateQuery } from './queries/useEventIncidentsUpdateQuery'
import { useEventScoutsQuery } from './queries/useEventScoutsQuery'
import { useQueueClient } from './queue/useQueueClient'
import { useEventId } from './useEventId'

/** Max cached incident `ut`, or current Unix seconds when the cache is empty. */
const incidentSyncTimestamp = (eventId: number) => {
  const incidents =
    queryClient.getQueryData<Incident[]>(['event', 'incidents', eventId]) ?? []
  const maxUt = incidents.reduce((acc, i) => Math.max(acc, i.ut), 0)
  return maxUt || Math.floor(Date.now() / 1000)
}

/** Plain `SyncOptions` for incident subscribe (order if known, else timestamp). */
const incidentSync = (eventId: number): SyncOptions => {
  const lastOrder = queryClient.getQueryData<number>([
    'event',
    'incidentLastOrder',
    eventId,
  ])

  if (lastOrder) {
    return { method: 'order', lastOrder }
  }

  // Overlap by 1s so the sync reply can include a message whose id becomes lastOrder.
  return {
    method: 'timestamp',
    lastTimestamp: (incidentSyncTimestamp(eventId) - 1) * 1000,
  }
}

/**
 * Incident socket batch → vue-query writes.
 * Open this file to see insert / update / delete + incidentsUpdate snapshot.
 */
export const writeIncidentBatch = (
  eventId: number,
  messages: RabbitIncidentMessage[],
) => {
  if (!messages.length) return

  const prev =
    queryClient.getQueryData<Incident[]>(['event', 'incidents', eventId]) ?? []

  // snapshot first — currentData must reflect state before this batch
  const byId = new Map(prev.map((i) => [i.id, i]))
  const incidentsUpdate: IncidentUpdate[] = messages.map((message) => {
    if (message.data.action === 'insert') return message
    const currentData = byId.get(message.data.id)
    return currentData ? { ...message, currentData } : message
  })

  const incidents = messages.reduce((list, message) => {
    const { action, id } = message.data

    if (action === 'insert') {
      if (list.some((i) => i.id === id)) return list
      return [...list, message.data]
    }

    if (action === 'update' || action === 'delete') {
      const idx = list.findIndex((i) => i.id === id)
      if (idx === -1) return list
      const next = list.slice()
      // Merge patch over existing; keys with undefined in the patch are dropped.
      next[idx] = Object.fromEntries([
        ...Object.entries(list[idx] as object).filter(
          ([k]) => !Object.hasOwn(message.data, k),
        ),
        ...Object.entries(message.data).filter(([, v]) => v !== undefined),
      ]) as Incident
      return next
    }

    return list
  }, prev)

  queryClient.setQueryData(['event', 'incidents', eventId], incidents)
  queryClient.setQueryData(
    ['event', 'incidentsUpdate', eventId],
    incidentsUpdate,
  )
}

export const incidentHandler = (
  eventId: number,
  messages: RabbitIncidentMessage[],
  acknowledgeIncidentUuids: (uuids: string[]) => void,
) => {
  if (!messages.length) return

  const lastOrder = queryClient.getQueryData<number>([
    'event',
    'incidentLastOrder',
    eventId,
  ])

  // Cold / timestamp-sync path: skip intentional overlap (already in HTTP cache),
  // but keep same-`ut` newcomers and newer updates to known ids.
  const cached =
    queryClient.getQueryData<Incident[]>(['event', 'incidents', eventId]) ?? []
  const cachedIds = new Set(cached.map((i) => i.id))
  const syncTs = incidentSyncTimestamp(eventId)
  const toApply = lastOrder
    ? messages
    : messages.filter((m) => {
        if (!cachedIds.has(m.data.id)) return true
        return m.ut > syncTs
      })

  if (toApply.length) {
    writeIncidentBatch(eventId, toApply)
  }

  // Cold-sync overlap skipped writeIncidentBatch — still resolve CQRS waiters.
  if (toApply.length < messages.length) {
    const applied = new Set(toApply)
    acknowledgeIncidentUuids(
      messages.filter((m) => !applied.has(m)).map((m) => m.uuid),
    )
  }

  // Learn order only from applied messages — do not advance past omitted overlap.
  if (toApply.length) {
    queryClient.setQueryData(
      ['event', 'incidentLastOrder', eventId],
      toApply[toApply.length - 1].id,
    )
  }
}

export const useIncidents = () => {
  const { data: incidents } = useEventIncidentsQuery()
  const { data: incidentsUpdate } = useEventIncidentsUpdateQuery()
  const { data: eventScouts } = useEventScoutsQuery()
  const { authInfo } = useAuth()

  const incidentIdIncidentIndexMap = computed(
    () =>
      new Map(
        (incidents.value ?? []).map((incident, index) => [incident.id, index]),
      ),
  )

  const userIncidentsUpdate = computed(() =>
    filterUserIncidentsUpdate(
      incidentsUpdate.value,
      (eventScouts.value as EventScout[] | undefined) ?? [],
      authInfo.value,
    ),
  )

  return {
    /** Reactive array of incidents, initially loaded via Sports API and updated through WebSocket */
    incidents: incidents as Ref<Incident[]>,

    /**
     * Raw incident data received via WebSocket as a differential array.
     *
     * Updated each time the queue gateway receives data from RabbitMQ.
     *
     * Contains only successfully confirmed incidents - failed updates are excluded.
     *
     * May contain multiple elements, but usually only contains a single element.
     */
    incidentsUpdate: incidentsUpdate as Ref<IncidentUpdate[]>,
    userIncidentsUpdate,

    /** Map of incident `id` to its corresponding `index` in the incidents array */
    incidentIdIncidentIndexMap,

    /** Retrieves an incident by `id` from the incidents array */
    getIncident: (id: number) => {
      const incidentIndex = incidentIdIncidentIndexMap.value.get(id)

      if (incidentIndex === undefined) {
        throw Error(`Incident with id ${id} not found`)
      }

      return toRaw((incidents as Ref<Incident[]>).value[incidentIndex])
    },
  }
}

export const useLiveIncidents = (
  acknowledgeIncidentUuids: (uuids: string[]) => void,
) => {
  const { connected, client } = useQueueClient()
  const eventId = useEventId()

  watch(
    connected,
    (isConnected, _, onCleanup) => {
      if (!isConnected) return
      onCleanup(
        client.subscribe(`events/${eventId.value}/incident`, {
          sync: incidentSync(eventId.value),
          onMessageBatch: (messages) =>
            incidentHandler(eventId.value, messages, acknowledgeIncidentUuids),
        }),
      )
    },
    { immediate: true },
  )
}
